import { FullStoryProvider } from '@goshippo/utilities';

import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LicenseInfo } from '@mui/x-license-pro';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ConnectedRouter } from 'connected-react-router';
import { type History } from 'history';
import { hot } from 'react-hot-loader';
import { I18nextProvider } from 'react-i18next';
import { compose } from 'redux';

import momentLocaleLoader from '~/components/momentLocaleLoader';
import { ScanDetectionMonitor } from '~/modules/scanEvents/components/ScanDetectionMonitor';
import { ShippoThemeProvider } from '~/themes/ShippoThemeProvider';
import { ShippoQueryClientProvider } from '~/utils/ShippoQueryClientProvider';
import TrackingContainer from '~/utils/TrackingHelper/TrackingContainer';
import ZendeskHelpContainer from '~/utils/ZendeskHelp/ZendeskHelpContainer';
import { AppcuesMonitor } from '~/utils/appcues/AppcuesMonitor';
import { FeatureFlagsProvider } from '~/utils/featureflags';
import { optimizelyClient } from '~/utils/featureflags/FeatureFlagsProvider';
import FullStoryMonitor from '~/utils/fullstory/FullStoryMonitor';
import i18n from '~/utils/i18n';
import { SegmentMonitor } from '~/utils/segment';
import { SiftMonitor } from '~/utils/sift';

import AppRoutes from './AppRoutes';
import { GlobalComponentsProvider } from './contexts/GlobalComponentsProvider';
import { OpenOrderContextProvider } from './pages/Orders/OpenOrderContextProvider';
import { NewRelicMonitor } from './utils/newRelic/NewRelicMonitor';
import { useTranscendScriptInject } from './utils/transcend/useTranscendScriptInject';

// It's expected that this will be in source control
// We call it once so our components are registered
LicenseInfo.setLicenseKey(
  '31b6b425575820bbe479c9218d591364T1JERVI6Mzg2MjAsRVhQSVJZPTE2Nzc0MzczNDgwMDAsS0VZVkVSU0lPTj0x',
);
interface AppProps {
  history: History;
}

const App = (props: AppProps) => {
  useTranscendScriptInject();

  return (
    <I18nextProvider i18n={i18n}>
      <FullStoryProvider fullStoryOpts={CONFIG.fullStory}>
        <ShippoQueryClientProvider>
          <FeatureFlagsProvider optimizelyClient={optimizelyClient}>
            <FullStoryMonitor />
            <SegmentMonitor />
            <AppcuesMonitor />
            <SiftMonitor />
            <NewRelicMonitor />
            <ScanDetectionMonitor />
            <ZendeskHelpContainer>
              <ConnectedRouter history={props.history}>
                <ShippoThemeProvider>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <TrackingContainer>
                      <OpenOrderContextProvider>
                        <GlobalComponentsProvider>
                          <AppRoutes />
                        </GlobalComponentsProvider>
                      </OpenOrderContextProvider>
                    </TrackingContainer>
                  </LocalizationProvider>
                </ShippoThemeProvider>
              </ConnectedRouter>
            </ZendeskHelpContainer>
          </FeatureFlagsProvider>
          <ReactQueryDevtools position="bottom-right" />
        </ShippoQueryClientProvider>
      </FullStoryProvider>
    </I18nextProvider>
  );
};

const AppWithHotModule = compose(hot(module), momentLocaleLoader)(App);

export default AppWithHotModule;
